import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Spinner from "../../../../../components/bootstrap/Spinner";
import IfoodRegisterWizard from "../../../../../components/modals/Delivery/Ifood/IfoodRegisterWizard";
import PreAlert from "../../../../../helpers/utils/preAlert";
import { ResultsContext } from "../../../../../contexts/results";
import { DefaultContext } from "../../../../../contexts/default";
import { useIfoodMenu } from "../../../../../hooks/useIfoodMenu";
import ProductsBlock from "./ifoodManager/ProductsBlock";
import ComplementsModalBlock from "./ifoodManager/ComplementsModalBlock";
import DeleteConfirmModal from "./ifoodManager/DeleteConfirmModal";
import {
    editItemPrice,
    setItemStatus,
    setComplementCategoryStatus,
    setComplementStatus,
    editComplementPrice,
    deleteCategory,
    desactiveItem,
    deleteComplementCategory,
    deleteComplement,
} from "../services/ifoodManagerApi";
import { extractProductIdsFromSubCategories, parseIfoodError, toInt } from "./ifoodManager/helpers";

const IfoodManager = ({ shouldOpenModal, sharedLocalData, loadingBaseData }) => {
    const { integrationsData, storeInfo } = useContext(ResultsContext);
    const { onShowAlert } = useContext(DefaultContext);

    const {
        estabSelected,
        storeSelected,
        categories = [],
        subCategories = [],
        productsById = {},
        complementCategoriesById = {},
        complementsById = {},
        storeProductsById = {},
        storeComplementsById = {},
    } = sharedLocalData || {};

    const [categoriesData, setCategoriesData] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState({});
    const [loadingPrices, setLoadingPrices] = useState({});
    const [touchedProductPrice, setTouchedProductPrice] = useState({});
    const [openComplementsModal, setOpenComplementsModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loadingComplementCategoryStatus, setLoadingComplementCategoryStatus] = useState({});
    const [loadingComplementStatus, setLoadingComplementStatus] = useState({});
    const [loadingComplementPrices, setLoadingComplementPrices] = useState({});
    const [draftComplementPrices, setDraftComplementPrices] = useState({});
    const [collapsedComplementCategories, setCollapsedComplementCategories] = useState({});
    const [openRegisterWizard, setOpenRegisterWizard] = useState(false);
    const [loadingDeleteCategory, setLoadingDeleteCategory] = useState({});
    const [loadingDeleteProduct, setLoadingDeleteProduct] = useState({});
    const [loadingDeleteComplementCategory, setLoadingDeleteComplementCategory] = useState({});
    const [loadingDeleteComplement, setLoadingDeleteComplement] = useState({});
    const [deleteModalData, setDeleteModalData] = useState({
        isOpen: false,
        type: null,
        row: null,
    });
    const [removedProductIds, setRemovedProductIds] = useState({});
    const [removedComplementCategoryIds, setRemovedComplementCategoryIds] = useState({});
    const [removedComplementIds, setRemovedComplementIds] = useState({});

    const showError = useCallback(
        (message) => {
            onShowAlert(PreAlert.error(message));
        },
        [onShowAlert]
    );

    const showSuccess = useCallback(
        (message) => {
            onShowAlert(PreAlert.success(message));
        },
        [onShowAlert]
    );

    const ifoodId = integrationsData?.ifood_id ?? null;
    const ifoodCatalog = integrationsData?.ifood_catalog ?? null;

    const { ifoodCategories, ifoodProducts, ifoodComplementsCategories, ifoodComplements, ifoodItems } = useIfoodMenu({
        estabSelected,
        storeSelected,
        ifood_id: ifoodId,
        ifood_catalog: ifoodCatalog,
    });

    useEffect(() => {
        if (!categories.length) {
            setCategoriesData([]);
            return;
        }

        const mountedCategories = categories
            .map((category) => {
                const ifoodCategory = ifoodCategories?.[category.id] || {};
                const isCategoryRegistered = Boolean(ifoodCategory?.ifoodId);

                const allProductIds = new Set(category.products || []);
                extractProductIdsFromSubCategories(category.subCategories || [], subCategories, allProductIds);

                const mountedProducts = Array.from(allProductIds)
                    .map((productId) => productsById[productId])
                    .filter(Boolean)
                    .map((product) => {
                        const storeProduct = storeProductsById[product.id] || {};
                        const ifoodProduct = ifoodProducts?.[product.id] || {};
                        const ifoodItem = ifoodItems?.[product.id] || {};
                        const isProductRegistered = !removedProductIds[product.id] && Boolean(ifoodItem?.ifoodId);

                        const price = toInt(storeProduct.price ?? product.price ?? 0);
                        return {
                            id: product.id,
                            categoryId: category.id,
                            name: product.name,
                            image_url: product.image_url || "",
                            delivery_code: product.delivery_code || "",
                            complements: product.complements || [],
                            ifoodPrice: price,
                            ifoodDiscount: toInt(ifoodItem.discountPrice),
                            ifoodId: ifoodProduct.ifoodId || null,
                            ifoodItemId: ifoodItem.ifoodId || null,
                            ifoodCategoryId: ifoodItem.ifoodCategoryId || null,
                            status: ifoodItem?.status === true,
                            isRegistered: isProductRegistered,
                        };
                    })
                    .filter((product) => product.isRegistered);

                return {
                    categoryId: category.id,
                    name: category.name || "",
                    products: mountedProducts,
                    isRegistered: isCategoryRegistered,
                    ifoodId: ifoodCategory?.ifoodId || null,
                };
            })
            .filter((category) => category.isRegistered && category.products.length > 0);

        setCategoriesData(mountedCategories);
    }, [categories, subCategories, productsById, storeProductsById, ifoodCategories, ifoodProducts, ifoodItems, removedProductIds]);

    const complementCategoriesByProduct = useMemo(() => {
        if (!selectedProduct) return [];
        const categoryIds = selectedProduct.complements || [];
        return categoryIds
            .map((id) => complementCategoriesById[id])
            .filter((item) => Boolean(item) && !removedComplementCategoryIds[item.id])
            .map((item) => {
                const ifoodCategory = ifoodComplementsCategories?.[item.id] || {};
                return {
                    id: item.id,
                    name: item.name || "",
                    min_item: toInt(item.min_item),
                    max_item: toInt(item.max_item),
                    delivery_code: item.delivery_code || "",
                    complements: item.complements || [],
                    ifoodId: ifoodCategory.ifoodId || null,
                    status: ifoodCategory.status === true,
                };
            });
    }, [selectedProduct, complementCategoriesById, ifoodComplementsCategories, removedComplementCategoryIds]);

    const getComplementsByCategory = useCallback(
        (complementCategory) => {
            if (!complementCategory) return [];
            return (complementCategory.complements || [])
                .map((id) => complementsById[id])
                .filter(Boolean)
                .map((item) => {
                    const ifoodComplement = ifoodComplements?.[item.id] || {};
                    const storeComplement = storeComplementsById[item.id] || {};
                    const ifoodId = ifoodComplement.ifoodId || null;
                    return {
                        id: item.id,
                        name: item.name || "",
                        image_url: item.image_url || "",
                        delivery_code: item.delivery_code || "",
                        ifoodId,
                        status: ifoodComplement.status === true,
                        ifoodPrice: toInt(
                            draftComplementPrices[item.id] ??
                            storeComplement.price ??
                            item.price ??
                            0
                        ),
                        ifoodDiscount: toInt(ifoodComplement.discountPrice ?? null),
                    };
                })
                .filter((item) => Boolean(item.ifoodId) && !removedComplementIds[item.id]);
        },
        [complementsById, ifoodComplements, storeComplementsById, draftComplementPrices, removedComplementIds]
    );

    useEffect(() => {
        if (shouldOpenModal) {
            setOpenRegisterWizard(true);
        }
    }, [shouldOpenModal]);

    const openProductComplementsModal = (row) => {
        setSelectedProduct(row);
        const collapsedByDefault = {};
        (row?.complements || []).forEach((categoryId) => {
            collapsedByDefault[categoryId] = true;
        });
        setCollapsedComplementCategories(collapsedByDefault);
        setOpenComplementsModal(true);
    };

    const openDeleteModal = (type, row) => {
        if (type === "category") {
            const hasRegisteredProducts = (row?.products || []).some((product) => product?.ifoodId || product?.ifoodItemId);
            if (hasRegisteredProducts) {
                showError("A categoria só pode ser removida quando não houver produtos cadastrados no iFood.");
                return;
            }
        }

        setDeleteModalData({
            isOpen: true,
            type,
            row,
        });
    };

    const closeDeleteModal = () => {
        setDeleteModalData({
            isOpen: false,
            type: null,
            row: null,
        });
    };

    const closeComplementsModal = () => {
        setOpenComplementsModal(false);
    };

    const toggleCollapseComplementCategory = (categoryId) => {
        setCollapsedComplementCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    const editPrice = (productId, masked) => {
        const cents = parseInt(String(masked).replace(/\D/g, ""), 10) || 0;
        setTouchedProductPrice((prev) => ({ ...prev, [productId]: true }));

        setCategoriesData((prev) =>
            prev.map((category) => ({
                ...category,
                products: category.products.map((product) =>
                    product.id === productId ? { ...product, ifoodPrice: cents } : product
                ),
            }))
        );
    };

    const editDiscount = (productId, masked) => {
        const cents = parseInt(String(masked).replace(/\D/g, ""), 10) || 0;

        setCategoriesData((prev) =>
            prev.map((category) => ({
                ...category,
                products: category.products.map((product) =>
                    product.id === productId ? { ...product, ifoodDiscount: cents } : product
                ),
            }))
        );
    };

    const savePrice = async (row) => {
        setLoadingPrices((prev) => ({ ...prev, [row.id]: true }));
        try {
            if (!row.ifoodItemId) {
                showError("Item ainda não cadastrado no iFood para enviar preço.");
                return;
            }

            const canonicalPrice = toInt(
                storeProductsById[row.id]?.ifoodPrice ??
                    storeProductsById[row.id]?.price ??
                    productsById[row.id]?.price ??
                    row.ifoodPrice
            );
            const priceToSend = touchedProductPrice[row.id] ? toInt(row.ifoodPrice) : canonicalPrice;

            await editItemPrice({
                itemId: row.ifoodItemId,
                newPrice: priceToSend,
                discountPrice: toInt(row.ifoodDiscount),
                storeInfo,
            });
            showSuccess("Preço enviado para iFood.");
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao salvar preço do produto."));
        } finally {
            setLoadingPrices((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const onComplementCategoryStatusToggle = async (categoryRow) => {
        setLoadingComplementCategoryStatus((prev) => ({ ...prev, [categoryRow.id]: true }));
        try {
            await setComplementCategoryStatus({
                storeInfo,
                optionGroupId: categoryRow.ifoodId,
                active: !categoryRow.status
            });
            showSuccess(`Categoria de complemento ${categoryRow.status ? "desativada" : "ativada"}.`);
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao atualizar categoria de complemento."));
        } finally {
            setLoadingComplementCategoryStatus((prev) => ({ ...prev, [categoryRow.id]: false }));
        }
    };

    const onComplementStatusToggle = async (row) => {
        setLoadingComplementStatus((prev) => ({ ...prev, [row.id]: true }));
        try {
            await setComplementStatus({
                storeInfo,
                optionId: row.ifoodId,
                active: !row.status
            });
            showSuccess(`Complemento ${row.status ? "desativado" : "ativado"} no iFood.`);
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao atualizar complemento."));
        } finally {
            setLoadingComplementStatus((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const onEditComplementPrice = (complementId, masked) => {
        const cents = parseInt(String(masked).replace(/\D/g, ""), 10) || 0;
        setDraftComplementPrices((prev) => ({ ...prev, [complementId]: cents }));
    };

    const onSaveComplementPrice = async (row) => {
        setLoadingComplementPrices((prev) => ({ ...prev, [row.id]: true }));
        try {
            if (!row.ifoodId) {
                showError("Complemento ainda não cadastrado no iFood para enviar preço.");
                return;
            }

            await editComplementPrice({
                complementId: row.ifoodId,
                newPrice: toInt(row.ifoodPrice),
                storeInfo,
            });
            showSuccess("Preço do complemento enviado para iFood.");
            setDraftComplementPrices((prev) => ({ ...prev, [row.id]: toInt(row.ifoodPrice) }));
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao salvar preço do complemento."));
        } finally {
            setLoadingComplementPrices((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const onDeleteCategory = async (categoryRow) => {
        setLoadingDeleteCategory((prev) => ({ ...prev, [categoryRow.categoryId]: true }));
        try {
            const hasRegisteredProducts = (categoryRow?.products || []).some((product) => product?.ifoodId || product?.ifoodItemId);
            if (hasRegisteredProducts) {
                showError("A categoria só pode ser removida quando não houver produtos cadastrados no iFood.");
                return;
            }

            if (!categoryRow.ifoodId) {
                showError("Categoria sem vínculo iFood para remover.");
                return;
            }

            await deleteCategory({
                storeInfo,
                categorieInfo: {
                    id: categoryRow.categoryId,
                    ifoodId: categoryRow.ifoodId,
                },
            });
            showSuccess("Categoria removida do iFood.");
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao remover categoria."));
        } finally {
            setLoadingDeleteCategory((prev) => ({ ...prev, [categoryRow.categoryId]: false }));
        }
    };

    const onDeleteProduct = async (row) => {
        setLoadingDeleteProduct((prev) => ({ ...prev, [row.id]: true }));
        try {
            if (!row.ifoodId) {
                showError("Produto sem vínculo iFood para remover.");
                return;
            }

            await desactiveItem({
                storeInfo,
                productInfo: {
                    id: row.id,
                    ifoodId: row.ifoodId,
                    status: true,
                    ifoodCategoryId: row.ifoodCategoryId,
                },
            });
            setRemovedProductIds((prev) => ({ ...prev, [row.id]: true }));
            showSuccess("Produto desativado no iFood.");
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao remover produto."));
        } finally {
            setLoadingDeleteProduct((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const onDeleteComplement = async (row) => {
        setLoadingDeleteComplement((prev) => ({ ...prev, [row.id]: true }));
        try {
            if (!row.ifoodId) {
                showError("Complemento sem vínculo iFood para remover.");
                return;
            }

            await deleteComplement({
                storeInfo,
                complementInfo: {
                    id: row.id,
                    ifoodId: row.ifoodId,
                },
            });
            setRemovedComplementIds((prev) => ({ ...prev, [row.id]: true }));
            showSuccess("Complemento removido do iFood.");
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao remover complemento."));
        } finally {
            setLoadingDeleteComplement((prev) => ({ ...prev, [row.id]: false }));
        }
    };

    const onDeleteComplementCategory = async (categoryRow) => {
        setLoadingDeleteComplementCategory((prev) => ({ ...prev, [categoryRow.id]: true }));
        try {
            if (!categoryRow.ifoodId) {
                showError("Categoria de complemento sem vínculo iFood para remover.");
                return;
            }

            await deleteComplementCategory({
                storeInfo,
                categorieInfo: {
                    id: categoryRow.id,
                    ifoodId: categoryRow.ifoodId,
                },
            });
            setRemovedComplementCategoryIds((prev) => ({ ...prev, [categoryRow.id]: true }));
            showSuccess("Categoria de complemento removida do iFood.");
        } catch (error) {
            showError(parseIfoodError(error, "Erro ao remover categoria de complemento."));
        } finally {
            setLoadingDeleteComplementCategory((prev) => ({ ...prev, [categoryRow.id]: false }));
        }
    };

    const handleConfirmDelete = async () => {
        const { type, row } = deleteModalData;
        if (!type || !row) return;

        if (type === "category") {
            await onDeleteCategory(row);
        }

        if (type === "product") {
            await onDeleteProduct(row);
        }

        if (type === "complement") {
            await onDeleteComplement(row);
        }

        if (type === "complementCategory") {
            await onDeleteComplementCategory(row);
        }

        closeDeleteModal();
    };

    const deleteModalTitle = useMemo(() => {
        if (deleteModalData.type === "category") return "Confirmar remoção de categoria";
        if (deleteModalData.type === "product") return "Confirmar remoção de produto";
        if (deleteModalData.type === "complement") return "Confirmar remoção de complemento";
        if (deleteModalData.type === "complementCategory") return "Confirmar remoção de categoria de complemento";
        return "Confirmar remoção";
    }, [deleteModalData.type]);

    const deleteModalLoading = useMemo(() => {
        if (deleteModalData.type === "category") {
            return loadingDeleteCategory[deleteModalData.row?.categoryId];
        }
        if (deleteModalData.type === "product") {
            return loadingDeleteProduct[deleteModalData.row?.id];
        }
        if (deleteModalData.type === "complement") {
            return loadingDeleteComplement[deleteModalData.row?.id];
        }
        if (deleteModalData.type === "complementCategory") {
            return loadingDeleteComplementCategory[deleteModalData.row?.id];
        }
        return false;
    }, [deleteModalData, loadingDeleteCategory, loadingDeleteProduct, loadingDeleteComplement, loadingDeleteComplementCategory]);

    const onProductStatusToggle = useCallback(
        async (row) => {
            setLoadingStatus((prev) => ({ ...prev, [row.id]: true }));

            try {
                if (!row.ifoodItemId) {
                    showError("Produto sem vínculo iFood para atualizar status.");
                    return;
                }

                await setItemStatus({
                    storeInfo,
                    itemId: row.ifoodItemId,
                    active: !row.status,
                });
                showSuccess(`Produto ${row.status ? "desativado" : "ativado"} no iFood.`);
            } catch (error) {
                showError(parseIfoodError(error, "Erro ao atualizar status no iFood."));
            } finally {
                setLoadingStatus((prev) => ({ ...prev, [row.id]: false }));
            }
        },
        [showError, showSuccess, storeInfo]
    );

    if (loadingBaseData) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner />
            </div>
        );
    }

    return (
        <>
            <ProductsBlock
                categoriesData={categoriesData}
                loadingStatus={loadingStatus}
                loadingPrices={loadingPrices}
                loadingDeleteCategory={loadingDeleteCategory}
                loadingDeleteProduct={loadingDeleteProduct}
                onEditPrice={editPrice}
                onEditDiscount={editDiscount}
                onSavePrice={savePrice}
                onToggleStatus={onProductStatusToggle}
                onOpenComplements={openProductComplementsModal}
                onRequestDeleteCategory={(category) => openDeleteModal("category", category)}
                onRequestDeleteProduct={(row) => openDeleteModal("product", row)}
            />

            <DeleteConfirmModal
                isOpen={deleteModalData.isOpen}
                title={deleteModalTitle}
                row={deleteModalData.row}
                loading={deleteModalLoading}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
            />

            <IfoodRegisterWizard
                open={openRegisterWizard}
                handleOpen={setOpenRegisterWizard}
                sharedLocalData={sharedLocalData}
                storeInfo={storeInfo}
                ifoodData={{
                    ifoodCategories,
                    ifoodProducts,
                    ifoodComplementsCategories,
                    ifoodComplements,
                    ifoodItems,
                }}
            />

            <ComplementsModalBlock
                isOpen={openComplementsModal}
                selectedProduct={selectedProduct}
                complementCategories={complementCategoriesByProduct}
                getComplementsByCategory={getComplementsByCategory}
                collapsedState={collapsedComplementCategories}
                onToggleCollapse={toggleCollapseComplementCategory}
                onClose={closeComplementsModal}
                loadingComplementCategoryStatus={loadingComplementCategoryStatus}
                loadingComplementStatus={loadingComplementStatus}
                loadingComplementPrices={loadingComplementPrices}
                loadingDeleteComplementCategory={loadingDeleteComplementCategory}
                loadingDeleteComplement={loadingDeleteComplement}
                onToggleComplementCategoryStatus={onComplementCategoryStatusToggle}
                onToggleComplementStatus={onComplementStatusToggle}
                onEditComplementPrice={onEditComplementPrice}
                onSaveComplementPrice={onSaveComplementPrice}
                onRequestDeleteComplementCategory={(category) => openDeleteModal("complementCategory", category)}
                onRequestDeleteComplement={(row) => openDeleteModal("complement", row)}
            />
        </>
    );
};

export default IfoodManager;
